import { useEffect, useCallback, useRef, useState, FC } from 'react'
import { ThemeProvider, CssBaseline } from '@material-ui/core'

import { isDebug, lmsRequired } from './configs'

import { useSessionContext, useDataContext } from './contexts'
import { getUser } from './User'

import {
    sendTerminateBeacon, getUserProfile, sendInitialize,
    getAllStudents, getAllModsForAllStudents
} from './api/LRS'
import { LMSInfo } from './api/ContentController'
import { UserModuleProfile, Student } from './interfaces/ModuleTypes'

import { LoadingOverlay, CriticalError, Main } from './components'
import Theme from './styles/Theme'
import { GlobalStyle } from './styles/GlobalStyle'

type LMSInfoEvent = {
    sid: string
    ctid: number
}

const App: FC = () => {
    const {
        user, userProfile, errorMessage,
        sessionActions: { setErrorMsg, setLMSAvailable, setUser, setProfile }
    } = useSessionContext()
    const { allProfiles, allStudents, dataActions: { setAllStudents, setAllProfiles } } = useDataContext()

    const [lmsInfo, setLMSInfo] = useState<LMSInfo | null>(null)

    const handleUnload = useCallback(() => {
        if (!user || !userProfile) return

        console.warn('App unload triggered. Exiting app...')
        try {
            user.userId && userProfile['module-registration-id'] && sendTerminateBeacon(user, userProfile['module-registration-id'])
        }
        catch (error) {
            console.log(error)
        }
    }, [user, userProfile])

    const handleCriticalError = useCallback((error: any) => {
        let errorMsg = 'An error has occurred.  Please contact your Learning Management System administrator. '
        isDebug && (errorMsg += error)
        setErrorMsg(errorMsg)
        handleUnload()
    }, [handleUnload, setErrorMsg])

    const handleCrossOriginMessage = useCallback((event: MessageEvent) => {
        // Must check your origin!
        if (!event.origin.includes('.tpcfusion.com')
            && !event.origin.includes('//localhost')
            && !event.origin.includes('.simutechmultimedia.net'))
            return
        if (!event.data)
            return
        const eventData = event.data as LMSInfoEvent
        if (eventData.sid && !lmsInfo) {
            isDebug && console.log('Cross Origin message received:', event)
            const newLMSInfo: LMSInfo = {
                sessionId: eventData.sid,
                courseTrackerId: eventData.ctid
            }

            if (!newLMSInfo.sessionId) return

            isDebug && console.log('LMS Info received!', newLMSInfo)
            setLMSInfo(newLMSInfo)
            setLMSAvailable(true)
        }
    }, [lmsInfo, setLMSAvailable])

    useEffect(() => {
        window.addEventListener('unload', handleUnload)
        window.addEventListener('message', handleCrossOriginMessage, false)
        return () => {
            window.removeEventListener('unload', handleUnload)
            window.removeEventListener('message', handleCrossOriginMessage)
        }
    }, [handleUnload, handleCrossOriginMessage])

    const userFetching = useRef(false)
    useEffect(() => {
        if (userFetching.current) return
        const timer = setTimeout(() => {
            if (!lmsInfo) {
                userFetching.current = true
                if (lmsRequired) {
                    handleCriticalError('LMS could not be found to retrieve user.')
                    return
                }
                const getDefaultUser = async () => {
                    const usr = await getUser()
                    setUser(usr)
                    setLMSInfo({
                        sessionId: usr.lmsSessionId,
                        courseTrackerId: usr.courseTrackerId
                    } as LMSInfo)
                }
                getDefaultUser()
                clearTimeout(timer)
            }
        }, 3500)
        return () => clearTimeout(timer)
    }, [handleCriticalError, lmsInfo, setUser])

    useEffect(() => {
        if (!lmsInfo || user || errorMessage || userFetching.current) return
        userFetching.current = true

        const getUserFromCC = async () => setUser(await getUser(lmsInfo))

        getUserFromCC()
            .catch(error => handleCriticalError(error.toString()))
    }, [errorMessage, handleCriticalError, lmsInfo, setUser, user])

    const userProfileFetching = useRef(false)

    useEffect(() => {
        if (!user || userProfile || errorMessage || userProfileFetching.current) return
        userProfileFetching.current = true
        isDebug && console.log('Init PROFILE effect.')

        const init = async () => {
            const profile = JSON.parse(await getUserProfile(user)).profile as UserModuleProfile
            await sendInitialize(user, profile['module-registration-id'])
            setProfile(profile)
        }
        init().catch((error) => handleCriticalError(error))

    }, [errorMessage, handleCriticalError, setProfile, user, userProfile])


    const fetchingStudents = useRef(false)
    const fetchingProfiles = useRef(false)

    useEffect(() => {
        if (!user?.lrsDbName || allStudents.length || errorMessage || fetchingStudents.current) return
        fetchingStudents.current = true
        isDebug && console.log('Data Context All Students effect.')

        const fetchData = async () => {
            const students: Student[] = await getAllStudents(user.lrsDbName)
            setAllStudents(students)
        }
        fetchData().catch((error) => handleCriticalError(error))

    }, [allStudents.length, errorMessage, handleCriticalError, setAllStudents, user?.lrsDbName])

    useEffect(() => {
        if (!user?.lrsDbName || allProfiles.length || errorMessage || fetchingProfiles.current) return
        fetchingProfiles.current = true
        isDebug && console.log('Data Context All Profiles effect.')

        const fetchData = async () => {
            const profiles: any[] = await getAllModsForAllStudents(user.lrsDbName)
            console.log('profiles', profiles)
            setAllProfiles(profiles)
        }
        fetchData().catch((error) => handleCriticalError(error))

    }, [allProfiles.length, errorMessage, handleCriticalError, setAllProfiles, user?.lrsDbName])

    if (errorMessage) return <CriticalError message={errorMessage} />
    const isReady = !!user

    return (
        <ThemeProvider theme={Theme}>
            <CssBaseline />
            <GlobalStyle />
            <LoadingOverlay
                loading={!isReady}
                text={'Loading...'}
            />
            <Main />
        </ThemeProvider>
    )
}

export default App